import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users } from "lucide-react";

const TYPE_OPTIONS = [
  { value: "EVERYONE", label: "Everyone" },
  { value: "ATTENDEE_TYPE", label: "By Attendee Type" },
  { value: "RECOGNITION", label: "By Recognition" },
  { value: "LEADERSHIP", label: "By Leadership" },
  { value: "CUSTOM_GROUP", label: "Custom Audience Group" },
];

// Reusable canonical audience_spec editor. Reuses the shared Batch A resolver
// for a live eligibility count so managers see exactly who will be targeted.
export function AudiencePicker({ eventId, value, onChange }) {
  const spec = value || { type: "EVERYONE" };
  const [vocab, setVocab] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [count, setCount] = useState(null);

  useEffect(() => {
    api.mgrClassVocab(eventId).then(setVocab).catch(() => {});
    api.mgrAudiences(eventId).then((r) => setAudiences(r.audiences || [])).catch(() => {});
  }, [eventId]);

  const refreshCount = useCallback(() => {
    setCount(null);
    api.mgrAudienceResolve(eventId, spec).then((r) => setCount(r.count)).catch(() => setCount(null));
  }, [eventId, JSON.stringify(spec)]);
  useEffect(() => { refreshCount(); }, [refreshCount]);

  const setType = (type) => {
    if (type === "EVERYONE") onChange({ type });
    else if (type === "CUSTOM_GROUP") onChange({ type, group_id: audiences[0]?.id || null });
    else onChange({ type, values: [] });
  };

  const toggleVal = (v) => {
    const vals = new Set(spec.values || []);
    vals.has(v) ? vals.delete(v) : vals.add(v);
    onChange({ ...spec, values: Array.from(vals) });
  };

  const valueList = spec.type === "ATTENDEE_TYPE" ? vocab?.attendee_types
    : spec.type === "RECOGNITION" ? vocab?.recognitions
      : spec.type === "LEADERSHIP" ? vocab?.leaderships : null;

  return (
    <div className="space-y-3" data-testid="audience-picker">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Audience</Label>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))] px-2.5 py-1 text-xs font-semibold" data-testid="audience-count">
          <Users className="h-3 w-3" /> {count == null ? "…" : `${count} eligible`}
        </span>
      </div>
      <Select value={spec.type} onValueChange={setType}>
        <SelectTrigger data-testid="audience-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>

      {valueList && (
        <div className="flex flex-wrap gap-2">
          {valueList.map((v) => {
            const on = (spec.values || []).includes(v);
            return (
              <button key={v} type="button" onClick={() => toggleVal(v)} data-testid={`audience-val-${v}`}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${on
                  ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                  : "bg-white text-[hsl(var(--primary))] border-[hsl(var(--border))] hover:bg-[hsl(var(--muted)/0.5)]"}`}>
                {v}
              </button>
            );
          })}
        </div>
      )}

      {spec.type === "CUSTOM_GROUP" && (
        <Select value={spec.group_id || ""} onValueChange={(gid) => onChange({ type: "CUSTOM_GROUP", group_id: gid })}>
          <SelectTrigger data-testid="audience-group-select"><SelectValue placeholder="Select a group" /></SelectTrigger>
          <SelectContent>
            {audiences.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.member_count} members</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

// Compact label for read-only display of a spec.
export function audienceText(spec) {
  if (!spec) return "Everyone (legacy)";
  if (spec.type === "EVERYONE") return "Everyone";
  if (spec.type === "CUSTOM_GROUP") return "Custom group";
  if (["ATTENDEE_TYPE", "RECOGNITION", "LEADERSHIP"].includes(spec.type)) {
    return (spec.values || []).join(", ") || spec.type;
  }
  return "Everyone";
}
